// UX122 (docs/build-briefs/UX122-no-modifier-tiles-on-the-floor-menu.md): which menu items the floor grid, its letter
// picker and its search list. Pure, no DOM. Owner rulings 2026-10-14: an 86'd drink or bottle does not show in the list,
// and neither does an item the floor badges OUT. LOW items stay. The OUT test is the one stockStateBadge uses for its
// Out badge (quantityState "out_of_stock", reached only when the item is not 86'd), so the two never disagree.

export interface MenuGridItem {
  kind: string;
  /** Present on a cocktail. */
  flagged86?: boolean;
  /** Present on a cocktail. False when the drink has no ingredients and no liquor selection, so it cannot be rung
   * (LOCFLOOR1, sweep B17). Absent counts as having a recipe. */
  hasRecipe?: boolean;
  /** Present on every other kind. */
  liquorProduct?: {
    flagged86: boolean;
    quantityState: string | null;
    /** Only read under shelf pricing (INVIMPORT2): a pour with no shelf has no price to ring. */
    shelf_tier?: string | null;
    selling?: { shelfTier: string | null; saleMechanism?: string } | undefined;
    saleMechanism?: string | null;
    unit_price_cents?: number | null;
  };
}

/** What the grid needs to know about the location: only its pricing model. */
export interface FloorMenuContext {
  pricingModel?: string | undefined;
}

/** Why an item is not on the floor grid, or undefined when it is. */
export type FloorMenuUnavailableReason = "86" | "out" | "no_recipe" | "no_shelf";

/** Under shelf pricing a per-ounce item with no shelf has no price, so the floor cannot sell it (INVIMPORT2). */
function lacksShelf(product: NonNullable<MenuGridItem["liquorProduct"]>): boolean {
  const mechanism = product.selling?.saleMechanism ?? product.saleMechanism ?? null;
  const wholeUnit = mechanism === "whole_unit" || (mechanism === null && product.unit_price_cents != null);
  if (wholeUnit) return false;
  const tier = product.selling === undefined ? product.shelf_tier : product.selling.shelfTier;
  return tier === null;
}

export function floorMenuUnavailableReason(
  item: MenuGridItem,
  context: FloorMenuContext = {},
): FloorMenuUnavailableReason | undefined {
  if (item.kind === "cocktail") {
    if (item.flagged86 === true) return "86";
    return item.hasRecipe === false ? "no_recipe" : undefined;
  }
  const product = item.liquorProduct;
  if (product === undefined) {
    return undefined;
  }
  if (product.flagged86) return "86";
  if (product.quantityState === "out_of_stock") return "out";
  return context.pricingModel === "shelf" && lacksShelf(product) ? "no_shelf" : undefined;
}

export function isOnFloorMenuGrid(item: MenuGridItem, context: FloorMenuContext = {}): boolean {
  return floorMenuUnavailableReason(item, context) === undefined;
}

/** LOCFLOOR1 (sweep B18, B17): the sentence a search shows for an item it found but the grid does not offer, so a
 * person who typed its name is told why it is not there instead of "No matches". */
export function floorMenuUnavailableText(name: string, reason: FloorMenuUnavailableReason): string {
  switch (reason) {
    case "86":
      return `${name} is 86'd`;
    case "out":
      return `${name} is out of stock`;
    case "no_recipe":
      return `${name}: No recipe yet, ask a manager`;
    case "no_shelf":
      return `${name}: No shelf set yet, ask a manager`;
  }
}

export function floorMenuGridItems<T extends MenuGridItem>(
  items: readonly T[],
  context: FloorMenuContext = {},
): T[] {
  return items.filter((item) => isOnFloorMenuGrid(item, context));
}

/** UX125: how many tiles Popular Now shows. */
export const POPULAR_NOW_LIMIT = 5;

/**
 * UX125: the Popular Now tiles. `rankedIds` is the server's ranking, best seller first. Only items the floor grid
 * shows (floorMenuGridItems) count, so a top seller that is 86'd or OUT is skipped and the next one takes its place.
 */
export function popularNowItems<T extends MenuGridItem & { id: string }>(
  rankedIds: readonly string[],
  items: readonly T[],
  limit: number = POPULAR_NOW_LIMIT,
  context: FloorMenuContext = {},
): T[] {
  const onGrid = new Map(floorMenuGridItems(items, context).map((item) => [item.id, item]));
  const picked: T[] = [];
  for (const id of rankedIds) {
    const item = onGrid.get(id);
    if (item !== undefined && !picked.includes(item)) {
      picked.push(item);
    }
    if (picked.length === limit) {
      break;
    }
  }
  return picked;
}
